// services/scoringRuleService.ts
import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy,
  Timestamp,
  writeBatch
} from "firebase/firestore";
import { db } from "../firebase/config";

// Types for Scoring Rules
export interface ScoringRule {
  id: string;
  name: string;
  description: string;
  scope: "global" | "section" | "trait";
  scopeId?: string;
  scopeName?: string;
  rawScoreCalculation: {
    method: "sum" | "average" | "weighted_sum" | "custom";
    formula: string;
  };
  reverseScoring: {
    enabled: boolean;
    formula: string;
  };
  traitAggregation: {
    method: "average" | "sum" | "weighted_average";
    weights?: Record<string, number>;
  };
  normalization: {
    enabled: boolean;
    minValue: number;
    maxValue: number;
    targetMin: number;
    targetMax: number;
    method: "min_max" | "z_score" | "percentile";
  };
  sectionScoring: {
    calculateSectionWise: boolean;
    sectionWeightage: Record<string, number>;
  };
  overallIndex: {
    enabled: boolean;
    formula: string;
    interpretation: string;
  };
  isActive: boolean;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface ScoringRuleForm {
  name: string;
  description: string;
  scope: "global" | "section" | "trait";
  scopeId: string;
  rawScoreCalculation: {
    method: "sum" | "average" | "weighted_sum" | "custom";
    formula: string;
  };
  reverseScoring: {
    enabled: boolean;
    formula: string;
  };
  traitAggregation: {
    method: "average" | "sum" | "weighted_average";
  };
  normalization: {
    enabled: boolean;
    minValue: number;
    maxValue: number;
    targetMin: number;
    targetMax: number;
    method: "min_max" | "z_score" | "percentile";
  };
  sectionScoring: {
    calculateSectionWise: boolean;
  };
  overallIndex: {
    enabled: boolean;
    formula: string;
    interpretation: string;
  };
  isActive: boolean;
}

const SCORING_RULES_COLLECTION = "scoringRules";

// Get all scoring rules
export const getScoringRules = async (): Promise<ScoringRule[]> => {
  try {
    const q = query(collection(db, SCORING_RULES_COLLECTION), orderBy("createdAt", "desc"));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        name: data.name || "",
        description: data.description || "",
        scope: data.scope || "global",
        scopeId: data.scopeId || "",
        scopeName: data.scopeName || "",
        rawScoreCalculation: data.rawScoreCalculation || {
          method: "average",
          formula: "sum(question_values) / count(questions)"
        },
        reverseScoring: data.reverseScoring || {
          enabled: true,
          formula: "(maxScale + 1) - value"
        },
        traitAggregation: data.traitAggregation || {
          method: "average",
          weights: {}
        },
        normalization: data.normalization || {
          enabled: true,
          minValue: 0,
          maxValue: 5,
          targetMin: 0,
          targetMax: 100,
          method: "min_max"
        },
        sectionScoring: data.sectionScoring || {
          calculateSectionWise: true,
          sectionWeightage: {}
        },
        overallIndex: data.overallIndex || {
          enabled: true,
          formula: "(sum(section_scores * section_weightage) / sum(weightage)) * 100",
          interpretation: "0-25: Low, 26-50: Moderate, 51-75: High, 76-100: Very High"
        },
        isActive: data.isActive || false,
        createdBy: data.createdBy || "",
        createdAt: data.createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
        updatedAt: data.updatedAt?.toDate?.()?.toISOString() || new Date().toISOString(),
      } as ScoringRule;
    });
  } catch (error) {
    console.error("Error fetching scoring rules:", error);
    throw error;
  }
};

// Get scoring rule by ID
export const getScoringRuleById = async (id: string): Promise<ScoringRule | null> => {
  try {
    const docRef = doc(db, SCORING_RULES_COLLECTION, id);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      const data = docSnap.data();
      return {
        id: docSnap.id,
        name: data.name || "",
        description: data.description || "",
        scope: data.scope || "global",
        scopeId: data.scopeId || "",
        scopeName: data.scopeName || "",
        rawScoreCalculation: data.rawScoreCalculation,
        reverseScoring: data.reverseScoring,
        traitAggregation: data.traitAggregation,
        normalization: data.normalization,
        sectionScoring: data.sectionScoring,
        overallIndex: data.overallIndex,
        isActive: data.isActive || false,
        createdBy: data.createdBy || "",
        createdAt: data.createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
        updatedAt: data.updatedAt?.toDate?.()?.toISOString() || new Date().toISOString(),
      } as ScoringRule;
    }
    return null;
  } catch (error) {
    console.error("Error fetching scoring rule:", error);
    throw error;
  }
};

// Get active scoring rule for a specific scope
export const getActiveScoringRule = async (scope: "global" | "section" | "trait", scopeId?: string): Promise<ScoringRule | null> => {
  try {
    let constraints = [];
    constraints.push(where("scope", "==", scope));
    constraints.push(where("isActive", "==", true));
    
    if (scopeId) {
      constraints.push(where("scopeId", "==", scopeId));
    } else {
      constraints.push(where("scopeId", "==", ""));
    }
    
    constraints.push(orderBy("createdAt", "desc"));
    constraints.push(orderBy("createdAt", "desc"));
    
    const q = query(collection(db, SCORING_RULES_COLLECTION), ...constraints);
    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      const data = querySnapshot.docs[0].data();
      return {
        id: querySnapshot.docs[0].id,
        name: data.name || "",
        description: data.description || "",
        scope: data.scope || "global",
        scopeId: data.scopeId || "",
        scopeName: data.scopeName || "",
        rawScoreCalculation: data.rawScoreCalculation,
        reverseScoring: data.reverseScoring,
        traitAggregation: data.traitAggregation,
        normalization: data.normalization,
        sectionScoring: data.sectionScoring,
        overallIndex: data.overallIndex,
        isActive: data.isActive || false,
        createdBy: data.createdBy || "",
        createdAt: data.createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
        updatedAt: data.updatedAt?.toDate?.()?.toISOString() || new Date().toISOString(),
      } as ScoringRule;
    }
    return null;
  } catch (error) {
    console.error("Error fetching active scoring rule:", error);
    throw error;
  }
};

// Create scoring rule
export const createScoringRule = async (ruleData: ScoringRuleForm, userId: string): Promise<string> => {
  try {
    const now = Timestamp.now();
    const docRef = await addDoc(collection(db, SCORING_RULES_COLLECTION), {
      ...ruleData,
      createdBy: userId,
      createdAt: now,
      updatedAt: now,
    });
    return docRef.id;
  } catch (error) {
    console.error("Error creating scoring rule:", error);
    throw error;
  }
};

// Update scoring rule
export const updateScoringRule = async (id: string, ruleData: Partial<ScoringRuleForm>): Promise<void> => {
  try {
    const docRef = doc(db, SCORING_RULES_COLLECTION, id);
    await updateDoc(docRef, {
      ...ruleData,
      updatedAt: Timestamp.now(),
    });
  } catch (error) {
    console.error("Error updating scoring rule:", error);
    throw error;
  }
};

// Delete scoring rule
export const deleteScoringRule = async (id: string): Promise<void> => {
  try {
    const docRef = doc(db, SCORING_RULES_COLLECTION, id);
    await deleteDoc(docRef);
  } catch (error) {
    console.error("Error deleting scoring rule:", error);
    throw error;
  }
};

// Toggle scoring rule active status
export const toggleScoringRuleStatus = async (id: string, isActive: boolean): Promise<void> => {
  try {
    const docRef = doc(db, SCORING_RULES_COLLECTION, id);
    await updateDoc(docRef, {
      isActive,
      updatedAt: Timestamp.now(),
    });
  } catch (error) {
    console.error("Error toggling scoring rule status:", error);
    throw error;
  }
};

// Bulk delete scoring rules
export const bulkDeleteScoringRules = async (ruleIds: string[]): Promise<void> => {
  try {
    const batch = writeBatch(db);
    ruleIds.forEach(id => {
      const docRef = doc(db, SCORING_RULES_COLLECTION, id);
      batch.delete(docRef);
    });
    await batch.commit();
  } catch (error) {
    console.error("Error bulk deleting scoring rules:", error);
    throw error;
  }
};  